import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Icon } from '../ui/Icon';
import { Chip, Dialog, IconButton } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';
import {
  addDays,
  DAY_PERIODS,
  describePeriod,
  deviceToday,
  formatRange,
  groupOf,
  isIsoDate,
  longDay,
  matchPeriod,
  monthCells,
  monthTitle,
  PERIOD_GROUPS,
  PERIOD_LABELS,
  periodRange,
  QUICK_PERIODS,
  rangeProblem,
  shortDay,
  type DateRange,
  type PeriodGroupId,
} from '../../utils/periods';

/**
 * P-075 · El selector global de periodos (FI-90).
 *
 * Dos formas de mostrarlo y UNA hoja para elegir:
 *   · `PeriodField`: un botón que dice el periodo («Este mes ▾»). Para las listas: abre la hoja con los
 *     atajos y, debajo, «Otro periodo».
 *   · `PeriodChips`: los atajos a la vista (Hoy · Ayer · Antier · Últimos 7 días · Este mes · Otro periodo).
 *     Para Reportes, donde el periodo es el único filtro. «Otro periodo» abre la misma hoja, sin atajos.
 *
 * La hoja es un `Dialog` del rediseño: el foco entra y vuelve a quien la abrió, Escape y el Atrás de
 * Android la cierran. «Otro periodo» es un solo selector: Día | Semana | Mes | Personalizado; el
 * personalizado es un calendario de rango (primer toque, el inicio; segundo, el fin).
 *
 * Qué NO decide: el «hoy» (cada pantalla pasa el suyo; por omisión, el día del equipo, como el
 * calendario de antes), los límites (cada pantalla pasa los suyos) ni cuándo se pide al servidor.
 */

export interface PeriodControlProps {
  /** El periodo aplicado. Vacío y vacío = todas las fechas. */
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** «Hoy» de esta pantalla ('YYYY-MM-DD'). Por omisión, el día del equipo. */
  today?: string;
  /** Ofrece «Todas las fechas» (sin límite), donde la pantalla ya lo permitía. */
  allowAll?: boolean;
  /** El rango más largo que la pantalla acepta, en días con los dos extremos. */
  maxDays?: number;
  /** Permite elegir días futuros (por omisión, no). */
  allowFuture?: boolean;
  /** `day`: un solo día (Hoy · Ayer · Antier o un día del calendario). */
  mode?: 'range' | 'day';
  /** El nombre del control. */
  label?: string;
  disabled?: boolean;
  className?: string;
}

const ALL_DATES: DateRange = { from: '', to: '' };

function useToday(today?: string) {
  return today ?? deviceToday();
}

// ─────────────────────────────────────────────────────────────────────────────
// Botón (listas)
// ─────────────────────────────────────────────────────────────────────────────

export function PeriodField({ value, onChange, today: todayProp, allowAll = false, maxDays, allowFuture = false, mode = 'range', label = 'Periodo', disabled = false, className }: PeriodControlProps) {
  const today = useToday(todayProp);
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const text = mode === 'day' ? describeDay(value.from, today) : describePeriod(value, today);
  return (
    <div className={cx('n3-period-field', className)}>
      <span className="n3-period-field__label" id={labelId}>{label}</span>
      <button
        aria-describedby={labelId}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`${label}: ${text}. Cambiar`}
        className="n3-period-field__trigger"
        disabled={disabled}
        onClick={() => setOpen(true)}
        type="button"
      >
        <span aria-hidden="true" className="n3-period-field__icon"><Icon name="calendar" size={18} /></span>
        <span className="n3-period-field__text">{text}</span>
        <span aria-hidden="true" className="n3-period-field__caret"><Icon name="chevron-down" size={16} /></span>
      </button>
      {open && (
        <PeriodSheet
          allowAll={allowAll}
          maxDay={allowFuture ? undefined : today}
          maxDays={maxDays}
          mode={mode}
          onClose={() => setOpen(false)}
          onPick={(range) => { setOpen(false); onChange(range); }}
          showQuick
          title={label}
          today={today}
          value={value}
        />
      )}
    </div>
  );
}

function describeDay(day: string, today: string) {
  if (!day) return 'Elige un día';
  const named = matchPeriod({ from: day, to: day }, today, DAY_PERIODS);
  return named ? PERIOD_LABELS[named] : shortDay(day, today);
}

// ─────────────────────────────────────────────────────────────────────────────
// Chips (Reportes)
// ─────────────────────────────────────────────────────────────────────────────

export function PeriodChips({ value, onChange, today: todayProp, maxDays, allowFuture = false, label = 'Periodo', className, children }: PeriodControlProps & { children?: ReactNode }) {
  const today = useToday(todayProp);
  const [open, setOpen] = useState(false);
  const sheetId = useId();
  const quick = matchPeriod(value, today, QUICK_PERIODS);
  const other = !quick;
  return (
    <div className={cx('n3-period-chips', className)}>
      <div aria-label={label} className="n3-period-chips__row" role="group">
        {QUICK_PERIODS.map((id) => (
          <Chip key={id} onClick={() => onChange(periodRange(id, today))} selected={quick === id}>{PERIOD_LABELS[id]}</Chip>
        ))}
        <Chip
          aria-controls={open ? sheetId : undefined}
          aria-expanded={open}
          aria-haspopup="dialog"
          icon={<Icon name="calendar" size={16} />}
          onClick={() => setOpen(true)}
          selected={other}
        >
          {other ? `Otro periodo: ${describePeriod(value, today)}` : 'Otro periodo'}
        </Chip>
        {children}
      </div>
      {open && (
        <PeriodSheet
          id={sheetId}
          maxDay={allowFuture ? undefined : today}
          maxDays={maxDays}
          mode="range"
          onClose={() => setOpen(false)}
          onPick={(range) => { setOpen(false); onChange(range); }}
          showQuick={false}
          title="Otro periodo"
          today={today}
          value={value}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// La hoja
// ─────────────────────────────────────────────────────────────────────────────

interface PeriodSheetProps {
  id?: string;
  title: string;
  value: DateRange;
  today: string;
  mode: 'range' | 'day';
  showQuick: boolean;
  allowAll?: boolean;
  maxDays?: number;
  maxDay?: string;
  onPick: (range: DateRange) => void;
  onClose: () => void;
}

export function PeriodSheet({ id, title, value, today, mode, showQuick, allowAll = false, maxDays, maxDay, onPick, onClose }: PeriodSheetProps) {
  const current = matchPeriod(value, today);
  const [group, setGroup] = useState<PeriodGroupId>(() => (value.from || value.to ? groupOf(current) : 'semana'));
  const fits = (range: DateRange) => rangeProblem(range, { maxDays, maxDay }) === null;
  const quickIds = mode === 'day' ? DAY_PERIODS : QUICK_PERIODS;
  const isAll = !value.from && !value.to;

  return (
    <Dialog onClose={onClose} title={mode === 'day' ? 'Elegir día' : title}>
      <div className="n3-period" id={id}>
        {showQuick && (
          <div aria-label="Atajos" className="n3-period__chips" role="group">
            {quickIds.map((periodId) => {
              const range = periodRange(periodId, today);
              return (
                <Chip disabled={!fits(range)} key={periodId} onClick={() => onPick(range)} selected={current === periodId}>
                  {PERIOD_LABELS[periodId]}
                </Chip>
              );
            })}
            {allowAll && mode === 'range' && <Chip onClick={() => onPick(ALL_DATES)} selected={isAll}>Todas las fechas</Chip>}
          </div>
        )}

        {mode === 'day' ? (
          <>
            {showQuick && <h3 className="n3-period__heading">Otro día</h3>}
            <RangeCalendar maxDay={maxDay} mode="day" onPick={onPick} today={today} value={value} />
          </>
        ) : (
          <>
            {showQuick && <h3 className="n3-period__heading">Otro periodo</h3>}
            <div aria-label="Tipo de periodo" className="n3-period__segments" role="group">
              {PERIOD_GROUPS.map((candidate) => (
                <Chip key={candidate.id} onClick={() => setGroup(candidate.id)} selected={group === candidate.id}>{candidate.label}</Chip>
              ))}
            </div>
            {group === 'personalizado' ? (
              <RangeCalendar maxDay={maxDay} maxDays={maxDays} mode="range" onPick={onPick} today={today} value={value} />
            ) : (
              <ul className="n3-period__options">
                {PERIOD_GROUPS.find((candidate) => candidate.id === group)!.periods.map((periodId) => {
                  const range = periodRange(periodId, today);
                  const selected = current === periodId;
                  const usable = fits(range);
                  return (
                    <li key={periodId}>
                      <button aria-pressed={selected} className={cx('n3-period__option', selected && 'n3-period__option--selected')} disabled={!usable} onClick={() => onPick(range)} type="button">
                        <span className="n3-period__option-text">
                          <span className="n3-period__option-label">{PERIOD_LABELS[periodId]}</span>
                          <span className="n3-period__option-range">{usable ? formatRange(range, today) : `Más de ${maxDays} días`}</span>
                        </span>
                        {selected && <span aria-hidden="true" className="n3-period__option-check"><Icon name="check" size={18} /></span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// El calendario
// ─────────────────────────────────────────────────────────────────────────────

const WEEKDAY_INITIALS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

interface RangeCalendarProps {
  value: DateRange;
  today: string;
  mode: 'range' | 'day';
  maxDays?: number;
  maxDay?: string;
  onPick: (range: DateRange) => void;
}

/**
 * Un solo calendario. En rango: el primer toque fija el inicio y el segundo el fin (si el segundo es
 * anterior, se invierten). En un día: un toque. Con el teclado: las flechas mueven un día o una semana,
 * Inicio y Fin van al lunes y al domingo, RePág y AvPág cambian de mes. Cada día se anuncia con su fecha
 * completa; lo que va eligiendo la persona se anuncia en una región viva.
 */
export function RangeCalendar({ value, today, mode, maxDays, maxDay, onPick }: RangeCalendarProps) {
  const anchor = isIsoDate(value.to) ? value.to : isIsoDate(value.from) ? value.from : today;
  const [view, setView] = useState(() => ({ year: Number(anchor.slice(0, 4)), month: Number(anchor.slice(5, 7)) - 1 }));
  const [start, setStart] = useState<string | null>(null);
  const [focusDay, setFocusDay] = useState(anchor);
  const [problem, setProblem] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const moveFocus = useRef(false);

  const cells = monthCells(view.year, view.month);
  const title = monthTitle(view.year, view.month);
  const shownFrom = start ?? (value.from || null);
  const shownTo = start ? null : (value.to || null);
  const focusInView = focusDay.slice(0, 7) === `${view.year}-${String(view.month + 1).padStart(2, '0')}`;
  const tabbable = focusInView ? focusDay : cells.find(Boolean)!;

  // Con el teclado, el foco sigue al día marcado (también al cambiar de mes: `onKeyDown` mueve el mes en el
  // mismo paso, así que el día ya está dibujado cuando esto corre).
  useLayoutEffect(() => {
    if (!moveFocus.current) return;
    moveFocus.current = false;
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${focusDay}"]`)?.focus();
  }, [focusDay, view]);

  const shiftMonth = (delta: number) => setView((current) => {
    const next = new Date(Date.UTC(current.year, current.month + delta, 1));
    return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
  });

  const disabledDay = (day: string) => Boolean(maxDay && day > maxDay);

  const pick = (day: string) => {
    if (disabledDay(day)) return;
    setFocusDay(day);
    if (mode === 'day') { onPick({ from: day, to: day }); return; }
    if (!start) { setStart(day); setProblem(null); return; }
    const range = start <= day ? { from: start, to: day } : { from: day, to: start };
    const found = rangeProblem(range, { maxDays, maxDay });
    if (found) { setProblem(found); return; }
    setStart(null);
    onPick(range);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const steps: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    let next: string | null = null;
    if (event.key in steps) next = addDays(focusDay, steps[event.key]!);
    else if (event.key === 'Home') next = addDays(focusDay, -((new Date(`${focusDay}T00:00:00Z`).getUTCDay() + 6) % 7));
    else if (event.key === 'End') next = addDays(focusDay, 6 - ((new Date(`${focusDay}T00:00:00Z`).getUTCDay() + 6) % 7));
    else if (event.key === 'PageUp' || event.key === 'PageDown') {
      const delta = event.key === 'PageUp' ? -1 : 1;
      const [year, month, day] = focusDay.split('-').map(Number) as [number, number, number];
      const target = new Date(Date.UTC(year, month - 1 + delta, 1));
      const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
      next = `${target.getUTCFullYear()}-${String(target.getUTCMonth() + 1).padStart(2, '0')}-${String(Math.min(day, last)).padStart(2, '0')}`;
    }
    if (!next) return;
    event.preventDefault();
    moveFocus.current = true;
    const year = Number(next.slice(0, 4));
    const month = Number(next.slice(5, 7)) - 1;
    if (year !== view.year || month !== view.month) setView({ year, month });
    setFocusDay(next);
  };

  const hint = problem
    ?? (mode === 'day'
      ? 'Toca el día.'
      : start ? `Desde el ${shortDay(start, today)}. Ahora toca el último día.` : 'Toca el primer día y luego el último.');

  return (
    <div className="n3-period__calendar">
      <div className="n3-period__month">
        <IconButton label="Mes anterior" onClick={() => shiftMonth(-1)} variant="soft"><Icon name="chevron-left" size={18} /></IconButton>
        <strong aria-live="polite" className="n3-period__month-title">{title}</strong>
        <IconButton label="Mes siguiente" onClick={() => shiftMonth(1)} variant="soft"><Icon name="chevron-right" size={18} /></IconButton>
      </div>
      <div aria-hidden="true" className="n3-period__weekdays">
        {WEEKDAY_INITIALS.map((initial, index) => <span key={`${initial}-${index}`}>{initial}</span>)}
      </div>
      <div aria-label={title} className="n3-period__grid" onKeyDown={onKeyDown} ref={gridRef} role="group">
        {cells.map((day, index) => {
          if (!day) return <span aria-hidden="true" className="n3-period__blank" key={`blank-${index}`} />;
          const isStart = shownFrom === day;
          const isEnd = shownTo === day;
          const inside = Boolean(shownFrom && shownTo && day > shownFrom && day < shownTo);
          const selected = isStart || isEnd || inside;
          const state = isStart && isEnd ? ', elegido' : isStart ? ', primer día' : isEnd ? ', último día' : inside ? ', dentro del periodo' : '';
          return (
            <button
              aria-current={day === today ? 'date' : undefined}
              aria-label={`${longDay(day)}${state}`}
              aria-pressed={selected}
              className={cx(
                'n3-period__day',
                day === today && 'n3-period__day--today',
                inside && 'n3-period__day--inside',
                (isStart || isEnd) && 'n3-period__day--edge',
              )}
              data-day={day}
              disabled={disabledDay(day)}
              key={day}
              onClick={() => pick(day)}
              onFocus={() => setFocusDay(day)}
              tabIndex={day === tabbable ? 0 : -1}
              type="button"
            >
              {Number(day.slice(8))}
            </button>
          );
        })}
      </div>
      <p aria-live="polite" className={cx('n3-period__hint', problem && 'n3-period__hint--problem')} role={problem ? 'alert' : undefined}>{hint}</p>
    </div>
  );
}
